import { useEffect, useRef } from "react";
import { useQueryClient } from "@tanstack/react-query";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL || "";
const WS_URL = BACKEND_URL.replace(/^http/, "ws") + "/ws";

// Maps the collection key the backend's change-stream watcher sends -> which react-query
// key(s) to invalidate. Uses the un-exact (prefix) match react-query already does by
// default, so invalidating "documents" also catches ["documents", leadId] queries, etc.
const KEY_MAP = {
  leads: [["leads"]],
  documents: [["documents"], ["documents-standalone"]],
  notifications: [["notifications"]],
  messages: [["chat"]],
  "chat-rooms": [["chat-rooms"]],
};

// One WebSocket connection for the whole app (mounted once in AppLayout), pushed to by the
// backend the moment any watched collection changes in MongoDB — regardless of which
// device/tab/worker process made the write. This is what makes "input di desktop, muncul di
// HP dalam <1 detik" actually true instead of waiting for the next manual refresh or
// react-query's normal refetch-on-focus. Auto-reconnects with backoff so a dropped mobile
// network connection (backgrounding the app, switching wifi/data) recovers on its own.
export default function useRealtimeSync() {
  const qc = useQueryClient();
  const retryRef = useRef(0);
  const wsRef = useRef(null);
  const closedByUsRef = useRef(false);

  useEffect(() => {
    if (!BACKEND_URL) return;
    closedByUsRef.current = false;

    const connect = () => {
      const token = localStorage.getItem("crm_token");
      if (!token) return; // not logged in yet — AuthContext will trigger a remount on login
      const ws = new WebSocket(`${WS_URL}?token=${encodeURIComponent(token)}`);
      wsRef.current = ws;

      ws.onopen = () => { retryRef.current = 0; };
      ws.onmessage = (ev) => {
        try {
          const msg = JSON.parse(ev.data);
          if (msg.type === "invalidate" && KEY_MAP[msg.key]) {
            KEY_MAP[msg.key].forEach((queryKey) => qc.invalidateQueries({ queryKey }));
          }
        } catch { /* ignore malformed frame */ }
      };
      ws.onclose = () => {
        if (closedByUsRef.current) return;
        // Exponential backoff capped at 15s — fast enough to feel "instant" again after a
        // brief hiccup, but won't hammer the server during a real outage.
        const delay = Math.min(15000, 1000 * 2 ** retryRef.current);
        retryRef.current += 1;
        setTimeout(connect, delay);
      };
      ws.onerror = () => ws.close();
    };

    connect();
    return () => {
      closedByUsRef.current = true;
      wsRef.current?.close();
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);
}
